import {
    check,
    maxLength,
    minLength,
    parse,
    pipe,
    regex,
    string,
    ValiError,
} from 'valibot';

/**
 * E-Invoice ID Validation Schema
 * - Only digits and ":"
 * - Cannot start or end with ":"
 * - No consecutive "::"
 * - Maximum 20 characters
 * - Examples: "5560897851", "0007:5560897851"
 */
export const einvoiceIdSchema = pipe(
	string('ID måste vara en text'),
	minLength(1, 'ID krävs'),
	maxLength(20, 'ID kan inte överstiga 20 tecken'),
	regex(/^[\d:]+$/, 'Endast siffror och ":" är tillåtna'),
	check((v) => !v.startsWith(':') && !v.endsWith(':'), '":" får inte vara i början eller slutet'),
	check((v) => !v.includes('::'), 'Två ":" i rad är inte tillåtet')
);

/**
 * Validate an e-invoice ID and return an error message if invalid.
 */
export const validateEinvoiceId = (id: string): { valid: boolean; error?: string } => {
	try {
		parse(einvoiceIdSchema, id);
		return { valid: true };
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Ogiltigt ID';
			return { valid: false, error: message };
		}
		return { valid: false, error: 'Ogiltigt ID' };
	}
};

/**
 * E-Invoice Exchange (E-fakturaväxel) Validation Schema
 * - Free text including spaces
 * - Maximum 30 characters
 */
export const einvoiceExchangeSchema = pipe(
	string('E-fakturaväxel måste vara en text'),
	minLength(1, 'E-fakturaväxel krävs'),
	maxLength(30, 'E-fakturaväxel kan inte överstiga 30 tecken')
);

/**
 * Validate an e-invoice exchange value and return an error message if invalid.
 */
export const validateEinvoiceExchange = (value: string): { valid: boolean; error?: string } => {
	try {
		parse(einvoiceExchangeSchema, value);
		return { valid: true };
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Ogiltig e-fakturaväxel';
			return { valid: false, error: message };
		}
		return { valid: false, error: 'Ogiltig e-fakturaväxel' };
	}
};

/**
 * GLN (Global Location Number) Validation Schema
 * - Only digits
 * - Exactly 13 digits
 * - Optional — only validate when a value is provided
 */
export const einvoiceGLNSchema = pipe(
	string('GLN måste vara en text'),
	regex(/^\d{13}$/, 'GLN måste vara exakt 13 siffror')
);

/**
 * Validate a GLN value and return an error message if invalid.
 * Returns valid:true for empty strings (field is optional).
 */
export const validateEinvoiceGLN = (value: string): { valid: boolean; error?: string } => {
	if (value.length === 0) return { valid: true };
	try {
		parse(einvoiceGLNSchema, value);
		return { valid: true };
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Ogiltigt GLN';
			return { valid: false, error: message };
		}
		return { valid: false, error: 'Ogiltigt GLN' };
	}
};
